import { memo, useMemo, useState } from 'react'

import { cn } from '@/lib/utils'

import { SidebarToggleButton } from './components/sidebar-toggle-button'
import { SidebarMenuItem } from './menu-item'
import { SidebarItem, SidebarThemes } from './types'
import { UserProfile } from './user-profile'

export interface SidebarProps {
  menuItems: SidebarItem[]
  theme?: SidebarThemes
}

export const Sidebar = memo(({ menuItems, theme = 'light' }: SidebarProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)

  const sidebarClassName = useMemo(
    () =>
      cn(
        'relative flex h-full flex-col justify-between shadow-md',
        'transition-all duration-300 shrink-0',
        isSidebarOpen ? 'w-sidebar-open' : 'w-24',
        theme === 'dark'
          ? 'bg-sidebar-primary text-sidebar-primary-foreground'
          : 'bg-background',
      ),
    [isSidebarOpen, theme],
  )

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen)

  const sidebarToggleAria = isSidebarOpen ? 'Close sidebar' : 'Open sidebar'

  if (menuItems?.length === 0) {
    return null
  }

  return (
    <div className={sidebarClassName}>
      <ul className="my-6 flex flex-col gap-2 overflow-x-hidden">
        {menuItems
          .filter((item) => !item.hidden)
          .map((item) => (
            <SidebarMenuItem
              key={item.index}
              item={item}
              isSidebarOpen={isSidebarOpen}
              theme={theme}
            />
          ))}
      </ul>

      <SidebarToggleButton
        isSidebarOpen={isSidebarOpen}
        onToggle={toggleSidebar}
        ariaLabel={sidebarToggleAria}
        theme={theme}
      />

      <UserProfile isSidebarOpen={isSidebarOpen} theme={theme} />
    </div>
  )
})

Sidebar.displayName = 'Sidebar'
